import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { User } from "lucide-react";
import Swal from "sweetalert2";
import { authHeaders } from "../../lib/auth";
import { getDepartments, getRoles } from "../../lib/api";
import { Department, Role } from "../../types";

interface EmployeeFormData {
  name: string;
  department_id: string;
  email: string | null;
  phone: string | null;
  blood_type: string | null;
  birth_date: string;
  gender: "male" | "female";
  height: number | null;
  username: string;
  password: string;
  role: string;
}

interface EmployeeFormProps {
  onSubmit: (newEmployeeId: string) => void;
}

export const EmployeeForm: React.FC<EmployeeFormProps> = ({ onSubmit }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    defaultValues: { role: "employee" },
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  useEffect(() => {
    getRoles()
      .then(setRoles)
      .catch((err) => console.error("Gagal memuat roles:", err));
    getDepartments()
      .then(setDepartments)
      .catch((err) => console.error("Gagal memuat departemen:", err));
  }, []);

  const onFormSubmit = async (data: EmployeeFormData) => {
    try {
      const payload = {
        ...data,
        email: data.email || null,
        phone: data.phone || null,
        blood_type: data.blood_type || null,
        username: data.username,
        password: data.password,
        role: data.role,
      };

      const res = await fetch(
        `${import.meta.env.VITE_API_BASE}/api/employees`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        throw new Error("Gagal menambahkan karyawan");
      }

      const newEmployee = await res.json();

      await Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: "Karyawan berhasil ditambahkan",
        confirmButtonText: "OK",
        timer: 2000,
        timerProgressBar: true,
      });

      reset();
      onSubmit(newEmployee.id);
    } catch (err) {
      console.error("Error:", err);

      Swal.fire({
        icon: "error",
        title: "Gagal!",
        text: "Terjadi kesalahan saat menambahkan karyawan.",
        confirmButtonText: "Tutup",
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <User className="h-5 w-5 mr-2 text-blue-600" />
        Tambah Karyawan Baru
      </h3>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nama Lengkap
          </label>
          <input
            {...register("name", { required: "Isi Nama lengkap" })}
            placeholder="Ahmad Susanto"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Departemen
          </label>
          <select
            {...register("department_id", { required: "Pilih departemen" })}
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Pilih Departemen</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.label}
              </option>
            ))}
          </select>
          {errors.department_id && (
            <p className="text-red-500 text-sm mt-1">
              {errors.department_id.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            {...register("email")}
            placeholder="ahmad.susanto@example.com"
            type="email"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            No Telp
          </label>
          <input
            {...register("phone")}
            placeholder="081234567890"
            type="number"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Golongan Darah
          </label>
          <input
            {...register("blood_type")}
            placeholder="A, B, AB, O"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.blood_type && (
            <p className="text-red-500 text-sm mt-1">
              {errors.blood_type.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tanggal Lahir
          </label>

          <input
            {...register("birth_date", {
              required: "Tanggal lahir wajib diisi",
            })}
            type="date"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          {errors.birth_date && (
            <p className="text-red-500 text-sm mt-1">
              {errors.birth_date.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jenis Kelamin
          </label>

          <select
            {...register("gender", {
              required: "Pilih jenis kelamin",
            })}
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Pilih Jenis Kelamin</option>
            <option value="male">Laki-laki</option>
            <option value="female">Perempuan</option>
          </select>

          {errors.gender && (
            <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tinggi Badan
          </label>

          <div className="relative">
            <input
              {...register("height", {
                required: "Tinggi badan wajib diisi",
                valueAsNumber: true,
                min: {
                  value: 100,
                  message: "Tinggi badan minimal 100 cm",
                },
                max: {
                  value: 250,
                  message: "Tinggi badan maksimal 250 cm",
                },
              })}
              type="number"
              step="0.1"
              placeholder="170"
              className="w-full rounded-lg border-gray-300 border px-4 py-3 pr-12 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
              cm
            </span>
          </div>

          {errors.height && (
            <p className="text-red-500 text-sm mt-1">{errors.height.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Username
          </label>
          <input
            {...register("username", { required: "Isi username" })}
            placeholder="ahmad.susanto"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.username && (
            <p className="text-red-500 text-sm mt-1">
              {errors.username.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            type="password"
            {...register("password", {
              required: "Isi password",
              minLength: {
                value: 6,
                message: "Password minimal 6 karakter",
              },
            })}
            placeholder="Password"
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Role
          </label>
          <select
            {...register("role")}
            className="w-full rounded-lg border-gray-300 border px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {roles.length === 0 && (
              <option value="employee">Employee</option>
            )}
            {roles.map((role) => (
              <option key={role.id} value={role.name}>
                {role.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 font-medium"
        >
          Tambah Karyawan
        </button>
      </form>
    </div>
  );
};
